import { useState, useEffect, useMemo } from "react";
import { useTheme } from "../../../stores/ThemeContext";
import { truncateString } from "../../../utils/functions";
import { T } from "@ihs-widget/utils/enums";
import { useLanguage } from "@ihs-widget/utils/hooks/useLanguage";
import { useTranslations } from "../../../translation/TranslationContext";
import SmallSpinnerTimeout from "@ihs-widget/components/common/SmallSpinnerTimeout";
import { useColorContext } from "@ihs-widget/stores/ColorContext";
import { useUrlParams } from "@ihs-widget/utils/hooks/useUrlParams";


interface MinimizedStatsConsoleProps {
    homeScore: number;
    awayScore: number;
    title: string;
}

export const MinimizedStatsConsole = ({ homeScore, awayScore, title }: MinimizedStatsConsoleProps) => {
    const [height, setHeight] = useState<string>('41px');
    const { theme, toggleTheme } = useTheme();
    const language = useLanguage();
    const translations = useTranslations(language);
    const { colors } = useColorContext();
    const { isIOS } = useUrlParams();

    const { goalsLineWidthHome, goalsLineWidthAway } = useMemo(() => {
        if (homeScore === null || awayScore === null) {
            return {
                goalsLineWidthHome: 50,
                goalsLineWidthAway: 50
            };
        }
        const totalScore = homeScore + awayScore;
        if (totalScore > 0) {
            return {
                goalsLineWidthHome: (homeScore / totalScore) * 100,
                goalsLineWidthAway: (awayScore / totalScore) * 100
            };
        } else {
            return {
                goalsLineWidthHome: 50,
                goalsLineWidthAway: 50
            };
        }
    }, [homeScore, awayScore]);

    useEffect(() => {
        const message = {
            type: 'suzu_stats_console_iframe',
            height: height,
            // width: width
        };
        window.parent.postMessage(message, '*'); 
    }, [height]);

    return (
        <div data-testid="stats-console-minimized-container" className="flex">
            <div className={`flex flex-row max-h-[${height}] w-full px-[8px] pt-[6.5px] pb-[8.5px] text-[12px] leading-[16px] font-medium`}
            style={{ 
                backgroundColor: theme === T.LIGHT ? '#FFFFFF' : colors?.backgroundColor,
                borderRadius: isIOS ? '4px' : '0px'
            }}
            >
                <div className="flex flex-col w-full gap-[6px]">
                    <div data-testid="stats-console-minimized-header" className='h-[16px] w-full flex justify-between items-center'>
                        {title !== null && homeScore !== null ? (
                            <div className=""
                                 style={{ color: theme === T.LIGHT ? '#111111' : '#FFFFFF' }}>
                                    {truncateString(homeScore, 3)} 
                                 </div>
                        ) 
                            : (<SmallSpinnerTimeout />)}
                        {title !== null ? (
                            <div className=''
                            style={{ color: theme === T.LIGHT ? colors?.textColor : '#FFFFFF' }}
                            >{truncateString(translations[`${title}` as keyof typeof translations] || '', 15)}</div>
                        ) : (<SmallSpinnerTimeout />)}
                        {title !== null && awayScore !== null ? (
                            <div className=''
                            style={{ color: theme === T.LIGHT ? '#111111' : '#FFFFFF' }}
                            >{truncateString(awayScore, 3)}</div>
                        ) : <SmallSpinnerTimeout />}
                    </div>
                    <div data-testid="stats-console-minimized-goals-line" className="w-full flex items-center gap-[2px]">
                        <div 
                            className={`h-[4px] rounded-[8px] ${goalsLineWidthHome === 0 ? 'hidden' : ''}`}
                            style={{ 
                                width: `${goalsLineWidthHome}%`,
                                backgroundColor: colors?.homeColor,
                                border: `0.6px solid ${colors?.borderColor}`
                            }}
                        />
                        <div
                            className={`h-[4px] rounded-[8px] ${goalsLineWidthAway === 0 ? 'hidden' : ''}`}
                            style={{ 
                                width: `${goalsLineWidthAway}%`,
                                backgroundColor: colors?.awayColor,
                                border: `0.6px solid ${colors?.borderColor}`
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}